import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Trophy, RotateCcw, Target, Clock, Zap, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react'
import { Game2048State } from '../../../types/game.types'
import { useApp } from '../../../contexts/AppContext'
import { generateId } from '../../../lib/storage'
import Button from '../../ui/Button'
import Card, { CardHeader, CardContent, CardFooter } from '../../ui/Card'
import Game2048Board from './Game2048Board'
import { createInitialState, makeMove, resetGame, GRID_SIZE, WINNING_VALUE } from './Game2048Logic'

export default function Game2048() {
  const [state, setState] = useState<Game2048State>(createInitialState())
  const [gameStartTime, setGameStartTime] = useState<number | null>(null)
  const [gameDuration, setGameDuration] = useState<number>(0)
  const { addGameResult } = useApp()
  
  // Game timer
  useEffect(() => {
    let interval: number | null = null
    
    if (!state.gameOver && gameStartTime) {
      interval = window.setInterval(() => {
        setGameDuration(Math.floor((Date.now() - gameStartTime) / 1000))
      }, 1000)
    } else if (state.gameOver && interval) {
      clearInterval(interval)
    }
    
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [state.gameOver, gameStartTime])
  
  // Initialize game start time
  useEffect(() => {
    if (!gameStartTime) {
      setGameStartTime(Date.now())
    }
  }, [gameStartTime])
  
  const handleMove = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    if (!gameStartTime) {
      setGameStartTime(Date.now())
    }
    
    const newState = makeMove(state, direction)
    setState(newState)
    
    // Save game result when game ends
    if (newState.gameOver && !state.gameOver) {
      const result = {
        id: generateId(),
        gameType: '2048' as const,
        score: newState.score,
        winner: (newState.won ? 'player' : null) as 'player' | null,
        duration: gameDuration,
        timestamp: Date.now(),
        moves: newState.moves,
        boardState: newState.grid
      }
      addGameResult(result)
    }
  }, [state, gameDuration, gameStartTime, addGameResult])
  
  const handleReset = () => {
    setState(resetGame())
    setGameStartTime(Date.now())
    setGameDuration(0)
  }
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (state.gameOver) return
      
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          handleMove('up')
          break
        case 'ArrowDown':
          e.preventDefault()
          handleMove('down')
          break
        case 'ArrowLeft':
          e.preventDefault()
          handleMove('left')
          break
        case 'ArrowRight':
          e.preventDefault()
          handleMove('right')
          break
        case 'r':
        case 'R':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()
            handleReset()
          }
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleMove, state.gameOver, handleReset])
  
  const getHighestTile = () => {
    let highest = 0
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        highest = Math.max(highest, state.grid[i][j])
      }
    }
    return highest
  }
  
  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Game Board */}
        <div className="lg:col-span-2">
          <Card shadow="lg" padding="lg">
            <CardHeader>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">2048</h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={RotateCcw}
                    onClick={handleReset}
                  >
                    New Game
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="flex flex-col items-center gap-8">
                {/* Score Display */}
                <div className="grid grid-cols-3 gap-4 w-full max-w-md">
                  <div className="text-center p-4 rounded-xl bg-surface border border-border">
                    <div className="text-sm text-text-muted mb-1">SCORE</div>
                    <div className="text-3xl font-bold">{state.score}</div>
                  </div>
                  
                  <div className="text-center p-4 rounded-xl bg-surface border border-border">
                    <div className="text-sm text-text-muted mb-1">BEST</div>
                    <div className="text-3xl font-bold">{state.bestScore}</div>
                  </div>
                  
                  <div className="text-center p-4 rounded-xl bg-surface border border-border">
                    <div className="text-sm text-text-muted mb-1">MOVES</div>
                    <div className="text-3xl font-bold">{state.moves}</div>
                  </div>
                </div>
                
                {/* Game Board */}
                <Game2048Board
                  state={state}
                  onMove={handleMove}
                />
                
                {/* Game Status */}
                <div className="text-center">
                  <motion.h3 
                    className={`text-2xl font-bold mb-2 ${
                      state.won ? 'text-success' : 
                      state.gameOver ? 'text-danger' : 
                      'text-text'
                    }`}
                    key={`${state.won}-${state.gameOver}`}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    {state.won ? '🎉 You Win! 🎉' : 
                     state.gameOver ? 'Game Over!' : 
                     'Keep playing!'}
                  </motion.h3>
                  
                  <div className="flex items-center justify-center gap-6 text-text-muted">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{gameDuration}s</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      <span>Highest: {getHighestTile()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      <span>Goal: {WINNING_VALUE}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Right Column - Game Info & Controls */}
        <div className="space-y-6">
          {/* Game Instructions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                How to Play
              </h3>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-text-muted">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>Use <strong>arrow keys</strong> to move all tiles in that direction</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>When two tiles with the same number touch, they <strong>merge into one</strong> with their sum</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>After each move, a new tile (2 or 4) appears in a random empty spot</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>Goal: Create a tile with the number <strong>{WINNING_VALUE}</strong>!</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          {/* Controls */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Controls</h3>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 flex justify-center">
                  <button
                    className="w-16 h-16 rounded-xl bg-surface border border-border flex items-center justify-center hover:bg-surface/80 transition-colors"
                    onClick={() => handleMove('up')}
                    aria-label="Move tiles up"
                  >
                    <ArrowUp className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="flex justify-center">
                  <button
                    className="w-16 h-16 rounded-xl bg-surface border border-border flex items-center justify-center hover:bg-surface/80 transition-colors"
                    onClick={() => handleMove('left')}
                    aria-label="Move tiles left"
                  >
                    <ArrowLeft className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="flex justify-center">
                  <button
                    className="w-16 h-16 rounded-xl bg-surface border border-border flex items-center justify-center hover:bg-surface/80 transition-colors"
                    onClick={() => handleMove('right')}
                    aria-label="Move tiles right"
                  >
                    <ArrowRight className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="col-span-2 flex justify-center">
                  <button
                    className="w-16 h-16 rounded-xl bg-surface border border-border flex items-center justify-center hover:bg-surface/80 transition-colors"
                    onClick={() => handleMove('down')}
                    aria-label="Move tiles down"
                  >
                    <ArrowDown className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="mt-4 text-center text-sm text-text-muted">
                <p>Or use <kbd className="px-2 py-1 bg-surface rounded border border-border mx-1">Arrow Keys</kbd> on your keyboard</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Game Stats */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Game Stats</h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-text-muted">Highest Tile</span>
                  <span className="font-bold">{getHighestTile()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-muted">Empty Tiles</span>
                  <span className="font-bold">
                    {state.grid.flat().filter(cell => cell === 0).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-muted">Game Time</span>
                  <span className="font-bold">{gameDuration}s</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-muted">Status</span>
                  <span className={`font-bold ${
                    state.won ? 'text-success' : 
                    state.gameOver ? 'text-danger' : 
                    'text-primary'
                  }`}>
                    {state.won ? 'Won' : 
                     state.gameOver ? 'Game Over' : 
                     'Playing'}
                  </span>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                variant="primary"
                fullWidth
                icon={RotateCcw}
                onClick={handleReset}
              >
                Start New Game
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  )
}